import React from 'react';
import styled from '@emotion/styled';
import { InputStyles } from './InputStyles';

type ToggleProps = {
  label?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  isEdit?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  compact?: boolean;
};

const ToggleButton = styled.button<{ active: boolean; disabled?: boolean; compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  background: transparent;
  line-height: 1.5;
  height: auto;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
`;

const ToggleSvg = styled.svg<{ $active: boolean; compact?: boolean }>`
  width: ${({ compact }) => (compact ? '20px' : '26px')};
  height: ${({ compact }) => (compact ? '11px' : '14px')};
  flex-shrink: 0;
`;

const ToggleTrack = styled.rect<{ $active: boolean }>`
  fill: ${({ $active, theme }) =>
    $active ? theme.colors.control.toggle.on : theme.colors.control.toggle.off};
  transition: fill 0.2s ease;
`;

const ToggleThumb = styled.circle<{ $active: boolean }>`
  fill: ${({ theme }) => theme.colors.control.toggle.thumb};
  transition: cx 0.2s ease;
`;

export const Toggle: React.FC<ToggleProps> = ({
  label,
  value,
  onChange,
  isEdit = false,
  disabled = false,
  id,
  name,
  compact = false,
}) => {
  const state = value ? 'ON' : 'OFF';
  const isDisabled = disabled || !isEdit;

  // 고유 ID 생성 (id가 없으면 label 기반으로 생성)
  const toggleId = id || (label ? `toggle-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  const renderToggleIcon = (isCompact: boolean = false) => (
    <ToggleSvg
      $active={value}
      compact={isCompact}
      viewBox="0 0 26 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ToggleTrack $active={value} width="26" height="14" rx="7" />
      <ToggleThumb $active={value} cx={value ? 19 : 7} cy="7" r="5" />
    </ToggleSvg>
  );

  // compact 모드: 여백 없는 간단한 레이아웃 (label은 외부에서 처리)
  if (compact) {
    return (
      <ToggleButton
        id={toggleId}
        name={name || toggleId}
        active={value}
        disabled={isDisabled}
        compact={true}
        onClick={isDisabled ? undefined : () => onChange(!value)}
      >
        {state}
        {renderToggleIcon(true)}
      </ToggleButton>
    );
  }

  if (isDisabled) {
    return (
      <InputStyles.InputContainer compact={compact}>
        <InputStyles.StyledInputRow>
          {label && <InputStyles.Label htmlFor={toggleId}>{label}</InputStyles.Label>}
          <InputStyles.InputWrapper compact={compact} hasLabel={!!label}>
            <ToggleButton
              id={toggleId}
              name={name || toggleId}
              active={value}
              disabled
              compact={compact}
            >
              {state}
              {renderToggleIcon()}
            </ToggleButton>
          </InputStyles.InputWrapper>
        </InputStyles.StyledInputRow>
      </InputStyles.InputContainer>
    );
  }

  return (
    <InputStyles.InputContainer compact={compact}>
      <InputStyles.StyledInputRow>
        {label && <InputStyles.Label htmlFor={toggleId}>{label}</InputStyles.Label>}
        <InputStyles.InputWrapper compact={compact} hasLabel={!!label}>
          <ToggleButton
            id={toggleId}
            name={name || toggleId}
            active={value}
            compact={compact}
            onClick={() => onChange(!value)}
          >
            {state}
            {renderToggleIcon()}
          </ToggleButton>
        </InputStyles.InputWrapper>
      </InputStyles.StyledInputRow>
    </InputStyles.InputContainer>
  );
};
